/* Navigation V2 -- vanilla JS enhancement layer. No framework, no jQuery.
   Reuses trapFocus/removeTrapFocus from assets/global.js (already loaded
   by layout/theme.liquid ahead of this file). Reads data-customer-state
   only -- never computes or infers authorization; that is decided
   server-side in Liquid.

   Progressive enhancement contract: each domain (Products, and later
   Systems/Brands/Dealer Resources/Project Desk) is a native
   <details>/<summary> in its own header-group section (see
   sections/nav-v2-products.liquid). That alone opens/closes, is fully
   keyboard-operable (Tab to the summary, Enter/Space toggles) and reveals
   every real <a> link underneath -- with zero JavaScript. Everything in
   this file only ENHANCES that working baseline: moving the whole
   <details> into the header's slot so it sits in the header's own nav
   row, hover-intent open/close, the backdrop, Escape-to-close with focus
   restoration, a focus trap while open, and collapsing each domain's
   multiple panes (all real/visible by default -- see
   nav-v2-products-panel.liquid) down to a rail + single active pane.
   None of this is required for basic navigation access.

   Lifecycle hardening (Phase 2.1, carried forward): the Shopify Theme
   Editor can connect/disconnect this element repeatedly -- on initial
   load, on a section re-render (fresh server HTML replacing this
   section, the same mechanism behind shopify:section:load), and on drag
   reordering. connectedCallback is guarded against re-wiring an already
   -wired instance, and disconnectedCallback tears down everything this
   instance did to shared/global state (the moved <details>, the
   document-level listeners) so a fresh instance never duplicates or
   orphans a stale one's DOM.

   Multi-domain hardening (Phase 3): every domain section (Products,
   Dealer Resources, and later Systems/Brands/Project Desk) independently
   includes this same <script> tag, so this whole file runs once per
   domain present on the page. `customElements.define` was already
   guarded (Phase 2.1) against being called twice, but that alone isn't
   enough -- a top-level `class` declaration itself cannot legally repeat
   in the same scope across two separately-executed <script> tags (unlike
   `var`/function declarations, this throws a SyntaxError, discovered by
   testing with two real domains active rather than assumed). Wrapping
   the whole file in an IIFE gives each execution its own scope so the
   class declaration itself never collides; only the customElements
   registration is global, and that guard still applies. */
(function () {

class NavV2 extends HTMLElement {
  constructor() {
    super();
    this.hoverOpenDelay = 90;
    this.hoverCloseDelay = 260;
    this.railHoverDelay = 140;   // dwell before a hovered rail category activates (a pass-through must not switch it)
    this.railTimer = null;
    this.closeTimer = null;
    this.openTimer = null;
    this._wired = false;
    this._hoverOpened = false;   // true only while the panel is open because of hover intent and has not been clicked
  }

  connectedCallback() {
    // Idempotency guard: connectedCallback can fire more than once for the
    // same instance and must never double-register listeners or
    // double-move its <details>.
    if (this._wired) return;
    this._wired = true;

    // Marks that JS actually ran. CSS gates the floating-overlay treatment
    // (position: fixed panel/backdrop) behind this class -- without it,
    // <details> stays a plain in-flow disclosure. That base state is not
    // a lesser fallback: a fixed-position panel with nothing to move its
    // own trigger out of the way visually covers that trigger once open,
    // so a second click meant to close it (clicking the summary again)
    // hits the panel instead and silently fails -- a real bug found by
    // testing "open, then close" without JS, not assumed. Idempotent and
    // harmless to set from every domain's connectedCallback.
    document.documentElement.classList.add('nav-v2-js-enhanced');

    const slot = document.querySelector('[data-nav-v2-slot]');
    const details = this.querySelector('.nav-v2__details');
    if (!details) return; // domain inactive (its own menu setting is blank)

    const domainKey = details.dataset.navDomainKey;
    this._slot = slot;
    this._details = details;
    this._domainItem = details.closest('[data-nav-v2-domain-item]');
    this._summary = details.querySelector(':scope > summary');
    this._backdrop = details.querySelector(':scope > .nav-v2__backdrop');
    this._panel = details.querySelector(':scope > .nav-v2__panel');

    if (slot && this._domainItem) {
      if (domainKey) {
        slot.querySelectorAll(`[data-nav-domain-key="${domainKey}"]`).forEach((existing) => {
          if (existing !== details) {
            const oldItem = existing.closest('[data-nav-v2-domain-item]');
            (oldItem || existing).remove();
          }
        });
      }
      if (!slot.contains(this._domainItem)) slot.appendChild(this._domainItem);
    }

    this._moveMobileFragment(domainKey);

    this._updatePanelTop();
    this._onResize = () => this._updatePanelTop();
    window.addEventListener('resize', this._onResize);

    this._collapseToSinglePane();
    this._wireRailButtons();

    if (this._summary && this._panel && this._backdrop) {
      // A click (or Enter/Space, which browsers dispatch as a synthetic
      // click on <summary>) is a deliberate, persistent open -- it must
      // stay open when the pointer subsequently leaves the trigger, unlike
      // a hover-open, which is transient and should auto-close on
      // mouseleave. Without this distinction, clicking to open and then
      // moving the mouse anywhere else (e.g. towards a link in the panel)
      // closed the panel out from under the user within ~260ms.
      this._openedByClick = false;
      this._summary.addEventListener('click', (event) => {
        // A hovering pointer opens the panel after the hover delay, so the dealer's click on the control then arrives at a panel that is
        // already open and the native toggle would close it: it reads as "the first click does not open the menu". A real pointer click
        // (event.detail >= 1) on a panel that is open only because of hover therefore pins it open instead of toggling it. A keyboard
        // Enter/Space activation reports detail 0 and still toggles normally, as does any click on a click-opened (pinned) panel,
        // so the second deliberate click closes it.
        if (this._details.open && this._hoverOpened && event.detail > 0) {
          event.preventDefault();
          this._hoverOpened = false;
          this._openedByClick = true;
          this._cancelClose();
          return;
        }
        this._hoverOpened = false;
        this._openedByClick = !this._details.open; // state before native toggle applies
        this._cancelClose();
      });
      this._summary.addEventListener('mouseenter', () => this._scheduleOpen());
      if (this._domainItem) {
        this._domainItem.addEventListener('mouseenter', () => this._scheduleOpen());
        this._domainItem.addEventListener('mouseleave', () => {
          if (!this._openedByClick) this._scheduleClose();
        });
      }
      this._summary.addEventListener('mouseleave', () => {
        if (!this._openedByClick) this._scheduleClose();
      });
      this._panel.addEventListener('mouseenter', () => this._cancelClose());
      this._panel.addEventListener('mouseleave', () => {
        if (!this._openedByClick) this._scheduleClose();
      });
      this._backdrop.addEventListener('click', () => this._close(true));

      // Native `toggle` fires whether the <details> was opened/closed by
      // click, keyboard, or JS setting .open -- this is the single hook
      // that keeps the backdrop and focus trap in sync regardless of how
      // it happened.
      this._onToggle = () => {
        if (details.open) {
          this._updatePanelTop();
          if (typeof trapFocus === 'function') trapFocus(this._panel);
        } else {
          this._hoverOpened = false;
          if (typeof removeTrapFocus === 'function') removeTrapFocus();
        }
      };
      details.addEventListener('toggle', this._onToggle);
    }

    // Listens on `details` itself, not `this` (<nav-v2>): the <details> is
    // moved out into the header's slot on connect, so it and its focused
    // summary/rail/links are no longer descendants of this element -- a
    // listener on `this` would never see a keydown that happens there.
    this._onKeydown = (event) => {
      if ((event.key === 'Escape' || event.code === 'Escape') && details.open) this._close(true);
    };
    details.addEventListener('keydown', this._onKeydown);

    // `name="nav-v2-domains"` on every domain's <details> makes browsers
    // that support it close sibling domains natively when one opens; this
    // is the fallback for browsers that don't, and is harmless where the
    // native behavior already applies.
    //
    // Also closes this panel when the header search overlay opens (a
    // separate, unrelated <details> inside <details-modal class=
    // "header__search">, not part of the nav-v2-domains sibling group) --
    // found live in production: hover-intent could pop a mega-menu panel
    // open while search was active, and since the panel is position:
    // fixed at a high z-index, it visually bled through the search
    // overlay. The CSS fix (nav-v2.css) makes the search overlay always
    // render on top regardless; this closes the panel outright so it
    // isn't left open (and focus-trapped) underneath.
    this._onDocumentToggleFallback = (event) => {
      if (event.target === details || !event.target.open) return;
      const isSiblingDomain = event.target.matches?.('.nav-v2__details[open]');
      const isSearchOverlay = event.target.closest?.('details-modal.header__search');
      if (isSiblingDomain || isSearchOverlay) details.open = false;
    };
    document.addEventListener('toggle', this._onDocumentToggleFallback, true);

    this._onDocumentClick = (event) => {
      if (!details.open) return;
      if (this.contains(event.target)) return;
      if (this._slot && this._slot.contains(event.target)) return;
      this._close(false);
    };
    document.addEventListener('click', this._onDocumentClick);
  }

  disconnectedCallback() {
    this._wired = false;
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
    clearTimeout(this.railTimer);

    if (this._onResize) window.removeEventListener('resize', this._onResize);
    if (this._onDocumentClick) document.removeEventListener('click', this._onDocumentClick);
    if (this._onDocumentToggleFallback) document.removeEventListener('toggle', this._onDocumentToggleFallback, true);
    if (this._details && this._onToggle) this._details.removeEventListener('toggle', this._onToggle);
    if (this._details && this._onKeydown) this._details.removeEventListener('keydown', this._onKeydown);

    // The <details> was moved into a slot that lives outside this element;
    // removing this element from the DOM does not remove it, so clean it
    // up explicitly or a torn-down instance leaves an orphaned, inert
    // trigger behind in the header.
    if (this._domainItem && this._domainItem.isConnected) {
      this._domainItem.remove();
    } else if (this._details && this._details.isConnected) {
      this._details.remove();
    }

    // Same cleanup for the mobile fragment moved into the shared drawer's
    // root list (see _moveMobileFragment) -- otherwise a torn-down domain
    // leaves an orphaned, inert row behind in the mobile menu too.
    if (this._mobileFragment && this._mobileFragment.isConnected) {
      this._mobileFragment.remove();
    }
  }

  // Phase 5: moves this domain's mobile drill-down fragment (rendered in
  // its own natural section position by snippets/nav-v2-mobile-fragment.
  // liquid -- real, functional, reachable with zero JS on its own) into
  // the shared mobile drawer's empty root list (snippets/nav-v2-mobile
  // -drawer.liquid), the same move-into-a-shared-container pattern
  // already used for desktop triggers. The shared drawer reuses Dawn's
  // own <header-drawer> custom element as-is; that class's constructor
  // already ran and wired listeners on whatever summary/button elements
  // existed in the DOM at that time, which does NOT include a fragment
  // moved in afterward. Re-running its full bindEvents() would be wrong
  // -- it re-scans the entire drawer, so previously-wired elements from
  // earlier domains would each pick up an extra, duplicate listener per
  // additional domain that mounts. Instead, only the newly-moved
  // fragment's own summary/button elements are wired, using the exact
  // same bound instance methods bindEvents() itself would have used.
  _moveMobileFragment(domainKey) {
    if (!domainKey) return;
    const root = document.querySelector('[data-nav-v2-mobile-root]');
    const fragment = document.querySelector(`[data-nav-v2-mobile-domain="${domainKey}"]`);
    if (!root || !fragment) return;

    root.querySelectorAll(`[data-nav-v2-mobile-domain="${domainKey}"]`).forEach((existing) => {
      if (existing !== fragment) existing.remove();
    });
    if (root.contains(fragment)) {
      this._mobileFragment = fragment;
      return;
    }

    root.appendChild(fragment);
    this._mobileFragment = fragment;

    const drawer = document.querySelector('header-drawer.nav-v2-mobile');
    if (drawer && typeof drawer.onSummaryClick === 'function') {
      fragment.querySelectorAll('summary').forEach((summary) => {
        summary.addEventListener('click', drawer.onSummaryClick.bind(drawer));
      });
      fragment
        .querySelectorAll('button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)')
        .forEach((button) => {
          button.addEventListener('click', drawer.onCloseButtonClick.bind(drawer));
        });
    }
  }

  _updatePanelTop() {
    const header = document.querySelector('.section-header, header');
    const bottom = header ? Math.round(header.getBoundingClientRect().bottom) : 84;
    document.documentElement.style.setProperty('--nav-v2-panel-top', `${Math.max(bottom, 0)}px`);
  }

  // Progressive enhancement: the server renders every domain's pane
  // visible/stacked (see nav-v2-products-panel.liquid) so they're all
  // reachable without JS. Once JS runs, collapse that down to the
  // original rail + single-active-pane look.
  _collapseToSinglePane() {
    const panel = this._panel;
    if (!panel) return;
    const railItems = Array.from(panel.querySelectorAll('.nav-v2__rail-item'));
    const panes = Array.from(panel.querySelectorAll('.nav-v2__pane-content'));
    if (!railItems.length || !panes.length) return;

    railItems.forEach((rail, index) => rail.setAttribute('aria-expanded', index === 0 ? 'true' : 'false'));
    railItems[0]?.classList.add('nav-v2__rail-item--active');

    panes.forEach((pane, index) => {
      pane.classList.toggle('nav-v2__pane-content--js-active', index === 0);
      if (index === 0) {
        pane.removeAttribute('hidden');
      } else {
        pane.setAttribute('hidden', '');
      }
    });
  }

  _wireRailButtons() {
    const panel = this._panel;
    if (!panel) return;
    const railItems = Array.from(panel.querySelectorAll('.nav-v2__rail-item'));
    railItems.forEach((rail) => {
      // Click, focus and arrow keys activate at once. Hover activates only after a short dwell, so a pointer that merely crosses neighbouring
      // categories on its way to the panel does not change the active category under the dealer.
      rail.addEventListener('click', () => { clearTimeout(this.railTimer); this._activateRail(rail, railItems); });
      rail.addEventListener('mouseenter', () => {
        clearTimeout(this.railTimer);
        if (rail.classList.contains('nav-v2__rail-item--active')) return;
        this.railTimer = setTimeout(() => this._activateRail(rail, railItems), this.railHoverDelay);
      });
      rail.addEventListener('mouseleave', () => clearTimeout(this.railTimer));
      rail.addEventListener('keydown', (event) => {
        const index = railItems.indexOf(rail);
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') clearTimeout(this.railTimer);
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          const next = railItems[Math.min(index + 1, railItems.length - 1)];
          next.focus();
          this._activateRail(next, railItems);
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          const prev = railItems[Math.max(index - 1, 0)];
          prev.focus();
          this._activateRail(prev, railItems);
        }
      });
    });
  }

  _activateRail(activeRail, railItems) {
    const panel = this._panel;
    const panes = Array.from(panel.querySelectorAll('.nav-v2__pane-content'));
    railItems.forEach((rail) => {
      const isActive = rail === activeRail;
      rail.classList.toggle('nav-v2__rail-item--active', isActive);
      rail.setAttribute('aria-expanded', isActive ? 'true' : 'false');
    });
    panes.forEach((pane) => {
      const isActive = pane.id === activeRail.getAttribute('aria-controls');
      pane.classList.toggle('nav-v2__pane-content--js-active', isActive);
      if (isActive) {
        pane.removeAttribute('hidden');
      } else {
        pane.setAttribute('hidden', '');
      }
    });
  }

  _scheduleOpen() {
    // Guards the reverse ordering from the toggle-fallback fix above: that
    // one reacts when search opens WHILE a nav-v2 panel is already open;
    // this stops hover-intent from opening a NEW panel while search is
    // already open (the exact sequence found live -- open search, then
    // move the mouse across a nav-v2 trigger on the way to the results).
    const searchOpen = document.querySelector('details-modal.header__search > details[open]');
    if (searchOpen) return;
    this._cancelClose();
    clearTimeout(this.openTimer);
    this.openTimer = setTimeout(() => {
      if (this._details.open) return;
      this._details.open = true;
      this._hoverOpened = true;
    }, this.hoverOpenDelay);
  }

  _scheduleClose() {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => this._close(false), this.hoverCloseDelay);
  }

  _cancelClose() {
    clearTimeout(this.closeTimer);
  }

  _close(restoreFocus) {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
    clearTimeout(this.railTimer);
    this._openedByClick = false;
    this._hoverOpened = false;
    if (!this._details.open) return;
    this._details.open = false; // fires the native `toggle` listener above
    if (restoreFocus && this._summary) this._summary.focus();
  }
}

if (!customElements.get('nav-v2')) {
  customElements.define('nav-v2', NavV2);
}
})();
